/** End-of-page hero image with staggered heading, and dark footer with HDE branding. */
import { Link } from '@remix-run/react';
import { cn } from '@rocco/utils/cn';
import { useInView } from 'react-intersection-observer';
import { ScrollReveal } from './components/ScrollReveal';
import { HDEDesktopFrame } from './components/HDEDesktopFrame';
import { HDEPresentedByMark } from './components/HDEPresentedByMark';
import { useParallax } from './hooks/use-parallax';

const HOMEPAGE_PATH = '/';

/** Large serif HDE letters SVG — reused from HeroBanner styling. */
const HdeLettersSvg = ({
    className,
    style,
}: {
    className?: string;
    style?: React.CSSProperties;
}) => (
    <svg
        className={className}
        style={style}
        viewBox="0 0 627.219 815.146"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M368.318 420.14L368.314 420.154C370.474 420.154 371.978 422.284 371.23 424.288L335.245 520.943C334.867 521.95 333.896 522.621 332.809 522.622H331.512C330.11 522.622 328.964 521.523 328.921 520.137C328.459 505.053 321.61 426.331 228.709 426.331H168.727C155.913 426.331 143.482 431.425 134.945 440.872C126.567 450.147 120.102 464.938 120.102 488.49V595.71H177.672C257.487 595.71 268.923 540.846 270.147 533.024C270.229 532.486 270.695 532.098 271.243 532.098H272.884C273.529 532.098 274.039 532.635 273.995 533.273L264.637 666.438C264.598 667.018 264.113 667.464 263.526 667.464H259.944C259.347 667.464 258.856 666.994 258.832 666.404C258.482 658.279 253.298 606.136 178.148 606.136H120.102V724.28C120.102 741.065 120.029 754.426 120.81 765.078C120.834 765.26 120.854 765.438 120.878 765.62C123.577 786.875 141.002 803.367 162.592 805.371C178.978 806.892 201.689 806.753 233.151 806.753C341.822 806.753 359.242 713.057 360.83 702.742C360.912 702.21 361.378 701.822 361.921 701.821H363.751C364.406 701.821 364.916 702.372 364.853 703.015L354.602 810.422C354.344 813.098 352.068 815.146 349.345 815.146H7.56685C6.67876 815.145 5.9607 814.436 5.96059 813.558V810.499C5.96059 809.602 6.69807 808.882 7.60075 808.911C15.8424 809.156 52.2897 807.161 52.2898 752.633V488.48C52.2898 426.711 7.83431 425.214 0.90288 425.358C0.40788 425.368 0.000139002 424.979 0 424.49V421.008C0 420.529 0.393103 420.14 0.878436 420.14H368.318Z"
            fill="white"
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M171.513 0C171.998 0 172.391 0.388408 172.391 0.867943V4.34984C172.391 4.83886 171.984 5.22717 171.489 5.21779C164.558 5.07871 120.102 6.57024 120.102 68.34V189.175H294.59V68.34C294.59 6.57024 250.134 5.07391 243.203 5.21779C242.708 5.2273 242.3 4.83894 242.3 4.34984V0.867943C242.3 0.388513 242.693 0.00017079 243.178 0H405.552C510 0 627.219 55.799 627.219 196.752C627.219 337.705 537.584 395.005 401.349 395.005H249.872C248.984 395.005 248.265 394.296 248.265 393.418V390.358C248.265 389.462 249.003 388.742 249.906 388.771C258.148 389.02 294.594 387.02 294.594 332.492V197.501H120.107V332.492C120.107 387.02 156.553 389.015 164.795 388.771C165.698 388.747 166.436 389.466 166.436 390.358V393.418C166.436 394.296 165.717 395.005 164.829 395.005H7.56685C6.67869 395.005 5.96059 394.296 5.96059 393.418V390.358C5.96059 389.462 6.69807 388.742 7.60075 388.771C15.8425 389.02 52.2898 387.02 52.2898 332.492V68.34C52.2897 6.57047 7.83432 5.07392 0.90288 5.21779C0.407816 5.22738 3.25909e-05 4.83899 0 4.34984V0.867943C5.22803e-05 0.388439 0.393135 5.16093e-05 0.878436 0H171.513ZM373.323 7.28012C369.377 7.28032 366.174 10.4408 366.174 14.3444V190.312C366.174 290.662 366.372 353.175 372.133 364.696C377.812 376.057 379.749 386.228 403.696 386.228C480.321 386.227 558.528 346.029 558.528 197.73C558.528 49.4303 455.832 7.2802 417.915 7.28012H373.323Z"
            fill="white"
        />
    </svg>
);

/** "HOME DESIGN EVENING." wordmark SVG. */
const TitleSvg = ({
    className,
    style,
}: {
    className?: string;
    style?: React.CSSProperties;
}) => (
    <svg
        className={className}
        style={style}
        viewBox="0 0 638 42"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M0 41.098V0.789262H8.74996V16.5745H25.7068V0.789262H34.4567V41.098H25.7068V23.3396H8.74996V41.098H0Z"
            fill="white"
        />
        <path
            d="M58.2122 42C45.4795 42 37.3933 33.0926 37.3933 21.0282C37.3933 8.96376 45.4795 0 58.2122 0C70.9449 0 78.9707 8.96376 78.9707 21.0282C78.9707 33.0926 70.9449 42 58.2122 42ZM58.2725 35.2913C66.1777 35.2913 70.0397 29.0336 70.0397 21.0282C70.0397 12.9664 66.1777 6.70873 58.2725 6.70873C50.3674 6.70873 46.3243 12.9664 46.3243 21.0282C46.3243 29.0336 50.3674 35.2913 58.2725 35.2913Z"
            fill="white"
        />
        <path
            d="M81.946 41.098V0.789262H94.2563L100.351 21.0282C101.86 26.102 103.067 31.9087 103.067 31.9087H103.187C103.187 31.9087 104.334 26.102 105.842 21.0282L111.937 0.789262H124.429V41.098H116.282V17.9839C116.282 14.7141 116.644 9.64027 116.644 9.64027H116.523C116.523 9.64027 115.558 14.3758 114.653 17.3074L107.23 41.098H98.8424L91.6011 17.3074C90.6959 14.3758 89.7304 9.64027 89.7304 9.64027H89.6097C89.6097 9.64027 89.9718 14.7141 89.9718 17.9839V41.098H81.946Z"
            fill="white"
        />
        <path
            d="M129.283 41.098V0.789262H161.025V7.66711H138.033V16.6872H158.188V23.396H138.033V34.2765H161.206V41.098H129.283Z"
            fill="white"
        />
        <path
            d="M173.16 41.098V0.789262H189.997C201.522 0.789262 208.945 9.47114 208.945 21.3664C208.945 27.906 206.591 33.6564 202.066 37.2081C198.867 39.6886 194.703 41.098 189.272 41.098H173.16ZM181.91 34.051H188.669C196.876 34.051 200.074 29.3718 200.074 21.3664C200.074 13.3611 196.333 7.77987 188.91 7.77987H181.91V34.051Z"
            fill="white"
        />
        <path
            d="M211.894 41.098V0.789262H243.635V7.66711H220.644V16.6872H240.799V23.396H220.644V34.2765H243.816V41.098H211.894Z"
            fill="white"
        />
        <path
            d="M263.866 42C252.159 42 245.522 37.1517 245.22 28.6389H253.789C254.392 33.4872 257.349 35.404 263.504 35.404C267.97 35.404 271.892 33.9383 271.892 30.2174C271.892 26.2711 267.789 25.4819 261.211 24.0725C253.487 22.4376 246.427 20.5208 246.427 12.1208C246.427 4.17181 253.366 0.0563751 262.961 0.0563751C272.677 0.0563751 278.952 4.51007 279.616 12.6282H271.228C270.746 8.56913 267.366 6.5396 262.901 6.5396C258.194 6.5396 255.116 8.4 255.116 11.2752C255.116 14.545 258.134 15.5034 264.53 16.8C273.401 18.604 280.642 20.5772 280.642 29.2591C280.642 37.4336 273.582 42 263.866 42Z"
            fill="white"
        />
        <path
            d="M284.118 41.098V0.789262H292.868V41.098H284.118Z"
            fill="white"
        />
        <path
            d="M329.367 41.098L329.065 35.6859H328.944C325.987 40.3087 321.944 41.9436 316.815 41.9436C310.298 41.9436 305.229 39.745 301.668 36.0805C298.108 32.3597 296.056 27.1732 296.056 21.0846C296.056 14.3758 298.591 8.51275 303.117 4.62282C306.617 1.6349 311.203 0 316.996 0C327.858 0 334.073 5.58121 335.461 13.4738H326.892C325.927 9.58389 322.729 6.7651 317.177 6.7651C309.03 6.7651 304.987 13.0792 304.987 21.0846C304.987 29.3154 309.634 35.3477 317.056 35.3477C323.755 35.3477 328.16 30.6685 328.16 26.2148V25.9893H317.78V19.6752H335.944V41.098H329.367Z"
            fill="white"
        />
        <path
            d="M340.251 41.098V0.789262H349.122L363.484 23.2832C364.872 25.4819 366.501 28.9208 366.501 28.9208H366.622C366.622 28.9208 366.38 24.749 366.38 21.9302V0.789262H375.01V41.098H366.561L351.837 18.8295C350.449 16.6872 348.76 13.2483 348.76 13.2483H348.639C348.639 13.2483 348.88 17.4765 348.88 20.2953V41.098H340.251Z"
            fill="white"
        />
        <path
            d="M388.136 41.098V0.789262H419.877V7.66711H396.885V16.6872H417.041V23.396H396.885V34.2765H420.058V41.098H388.136Z"
            fill="white"
        />
        <path
            d="M435.032 41.098L420.489 0.789262H429.661L437.264 24.1289C438.109 26.7785 439.135 30.5557 439.135 30.5557H439.256C439.256 30.5557 440.282 26.7221 441.127 24.1289L448.67 0.789262H457.54L442.997 41.098H435.032Z"
            fill="white"
        />
        <path
            d="M459.179 41.098V0.789262H490.921V7.66711H467.929V16.6872H488.084V23.396H467.929V34.2765H491.102V41.098H459.179Z"
            fill="white"
        />
        <path
            d="M494.731 41.098V0.789262H503.601L517.963 23.2832C519.351 25.4819 520.981 28.9208 520.981 28.9208H521.101C521.101 28.9208 520.86 24.749 520.86 21.9302V0.789262H529.489V41.098H521.041L506.317 18.8295C504.929 16.6872 503.239 13.2483 503.239 13.2483H503.119C503.119 13.2483 503.36 17.4765 503.36 20.2953V41.098H494.731Z"
            fill="white"
        />
        <path d="M534.35 41.098V0.789262H543.1V41.098H534.35Z" fill="white" />
        <path
            d="M547.978 41.098V0.789262H556.848L571.21 23.2832C572.598 25.4819 574.228 28.9208 574.228 28.9208H574.348C574.348 28.9208 574.107 24.749 574.107 21.9302V0.789262H582.736V41.098H574.288L559.564 18.8295C558.176 16.6872 556.486 13.2483 556.486 13.2483H556.366C556.366 13.2483 556.607 17.4765 556.607 20.2953V41.098H547.978Z"
            fill="white"
        />
        <path
            d="M619.157 41.098L618.855 35.6859H618.735C615.778 40.3087 611.735 41.9436 606.605 41.9436C600.088 41.9436 595.019 39.745 591.459 36.0805C587.899 32.3597 585.847 27.1732 585.847 21.0846C585.847 14.3758 588.381 8.51275 592.907 4.62282C596.407 1.6349 600.993 0 606.786 0C617.648 0 623.864 5.58121 625.252 13.4738H616.683C615.717 9.58389 612.519 6.7651 606.967 6.7651C598.821 6.7651 594.778 13.0792 594.778 21.0846C594.778 29.3154 599.424 35.3477 606.847 35.3477C613.545 35.3477 617.95 30.6685 617.95 26.2148V25.9893H607.571V19.6752H625.735V41.098H619.157Z"
            fill="white"
        />
        <path d="M629.19 41.098V32.9235H638V41.098H629.19Z" fill="white" />
    </svg>
);

/** Right-pointing arrow icon. */
const ArrowSvg = ({
    className,
    style,
}: {
    className?: string;
    style?: React.CSSProperties;
}) => (
    <svg
        className={className}
        style={style}
        viewBox="0 0 24 21"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M24 9.04537H5.85484L13.2 2.05765L11.0371 0L0 10.5L11.0371 21L13.2 18.9423L5.85484 11.9546H24V9.04537Z"
            fill="white"
        />
    </svg>
);

/** ArchiPro logo mark. */
const ArchiProLogo = ({
    className,
    style,
}: {
    className?: string;
    style?: React.CSSProperties;
}) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="122"
        height="90"
        viewBox="0 0 122 90"
        fill="none"
        className={className}
        style={style}
    >
        <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M121.184 89.0998C115.773 77.9904 110.369 66.8843 104.968 55.7878C104.886 55.6046 104.747 55.3892 104.52 55.2638C104.293 55.1385 104.039 55.1063 103.739 55.1063H84.9741C95.4469 45.6267 102.129 32.2158 102.557 17.278C102.58 16.439 101.882 15.7511 101.019 15.7511H85.4483C85.4483 36.7676 67.9938 53.8077 46.4588 53.8077H41.5616C47.6213 41.345 53.6712 28.8824 59.7144 16.4133C59.7967 16.2172 59.9252 16.0115 60.1557 15.8893C60.3862 15.7672 60.6332 15.7543 60.9428 15.7543H85.4945L85.4714 15.6964L76.6585 0V0.00642902H44.2226C44.002 0.00642902 43.8044 0.0225016 43.6298 0.112508C43.4553 0.205729 43.3532 0.356811 43.2873 0.504678C34.0101 19.5989 24.7229 38.7188 15.4292 57.8644C10.3706 68.273 5.31541 78.6848 0.256879 89.0998C0.194306 89.2155 0.148199 89.3376 0.0955061 89.4694C0.0658663 89.5466 0.0329332 89.6269 0 89.7105H23.5209C23.7086 89.7266 23.8996 89.6848 24.0544 89.6012C24.2092 89.5144 24.3178 89.3859 24.3574 89.2412C27.4728 82.8025 30.5784 76.3928 33.6774 70.0088C33.7598 69.832 33.8816 69.6488 34.0924 69.5395C34.3032 69.4302 34.4876 69.4238 34.807 69.4238H86.6504C86.9732 69.4238 87.1345 69.4366 87.3387 69.5395C87.5429 69.6456 87.6549 69.8095 87.7438 69.9863C90.8494 76.3671 93.9451 82.7511 97.0276 89.1319C97.0902 89.2573 97.2022 89.4501 97.3833 89.5498C97.5645 89.6494 97.7258 89.6623 98.0091 89.6687H121.441L121.184 89.0933V89.0998Z"
            fill="#F0F0F0"
        />
    </svg>
);

interface ClosingCTAProps {
    heroImage: string;
    onRegister?: () => void;
    eventDateText?: string;
    eventLocationText?: string;
    showRegisterButton?: boolean;
    isMobile?: boolean;
    /** When false, hides “Presented by” and sponsor mark (e.g. no active register-module event). */
    showPresentedBySponsor?: boolean;
    presentedByLogoUrl?: string | null;
    presentedByProfilePath?: string | null;
    presentedBySponsorName?: string | null;
}

export const ClosingCTA = ({
    heroImage,
    onRegister,
    eventDateText = '',
    eventLocationText = '',
    showRegisterButton = true,
    isMobile = false,
    showPresentedBySponsor = false,
    presentedByLogoUrl,
    presentedByProfilePath,
    presentedBySponsorName,
}: ClosingCTAProps) => {
    const { ref: sectionRef, inView } = useInView({
        triggerOnce: true,
        threshold: 0.15,
    });
    const { ref: imageRef, offsetY } = useParallax<HTMLImageElement>({
        speed: 0.15,
    });

    if (isMobile) {
        return (
            <section
                ref={sectionRef}
                data-testid="closing-cta"
                className="relative w-full overflow-hidden"
            >
                <div className="relative h-[25rem] w-full">
                    <img
                        src={heroImage}
                        alt="Home Design Evening"
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/15" />
                    <div className="absolute left-[1.5rem] top-[18.25rem]">
                        <p className="font-display text-[1.625rem] font-medium uppercase leading-[0.95] tracking-[-0.02em] text-white">
                            Your building
                        </p>
                        <p className="font-display text-[1.625rem] font-medium uppercase leading-[0.95] tracking-[-0.02em] text-white">
                            journey starts
                        </p>
                        <p className="font-display text-[1.625rem] font-medium uppercase leading-[0.95] tracking-[-0.02em] text-white">
                            with one evening.
                        </p>
                    </div>
                </div>
                <div className="relative h-[40rem] bg-[#030c0b] px-[1.5rem] pt-[2.5rem]">
                    <HdeLettersSvg
                        className="absolute left-[1.5rem] top-[2.5rem]"
                        style={{ width: '11.25rem', height: 'auto' }}
                    />
                    <TitleSvg
                        className="absolute right-[2.75rem]"
                        style={{ top: '23.15rem', width: '20.5rem' }}
                    />
                    <div className="absolute left-[14.75rem] top-[24.75rem]">
                        <p className="font-display text-[0.96875rem] font-medium uppercase leading-[0.9] tracking-[-0.03em] text-[#f0f0f0]">
                            {eventDateText}
                        </p>
                        <p className="font-display text-[0.96875rem] font-medium uppercase leading-[0.9] tracking-[-0.03em] text-[#f0f0f0]">
                            {eventLocationText}
                        </p>
                        <button
                            type="button"
                            onClick={onRegister}
                            className={`mt-[1.9rem] inline-flex items-center gap-[0.375rem] ${showRegisterButton ? '' : 'pointer-events-none invisible'}`}
                        >
                            <span className="font-display text-[0.96875rem] font-medium uppercase leading-[0.9] tracking-[-0.03em] text-[#f0f0f0]">
                                REGISTER NOW
                            </span>
                            <ArrowSvg
                                className="rotate-180"
                                style={{
                                    width: '0.75rem',
                                    height: '0.65625rem',
                                }}
                            />
                        </button>
                        <p className="mt-[0.68rem] text-[0.4375rem] uppercase leading-[0.9] tracking-[0.03em] text-[#f0f0f0]">
                            FREE ENTRY
                        </p>
                        <p className="mt-[0.25rem] text-[0.4375rem] uppercase leading-[0.9] tracking-[0.03em] text-[#f0f0f0]">
                            LIMITED SPACES
                        </p>
                    </div>
                    {showPresentedBySponsor ? (
                        <div className="absolute bottom-[2.85rem] right-[13.5rem] flex items-start gap-[1rem]">
                            <p className="mt-[1.6rem] font-sans text-[0.42rem] font-medium uppercase leading-[0.9] tracking-[0.08em] text-[#f0f0f0]">
                                Presented by
                            </p>
                            <HDEPresentedByMark
                                logoUrl={presentedByLogoUrl}
                                profilePath={presentedByProfilePath}
                                sponsorName={presentedBySponsorName}
                                imageClassName="max-h-[1.75rem] w-[5.75rem]"
                                fallbackLogoClassName="h-auto w-[5.75rem] text-[#f0f0f0]"
                            />
                        </div>
                    ) : null}
                    <div className="absolute bottom-[2.75rem] right-[1.5rem] flex items-end gap-[0.5rem]">
                        <span className="mb-2 text-[0.42rem] font-medium uppercase leading-[0.9] text-[#f0f0f0]">
                            BY ARCHIPRO
                        </span>
                        <Link
                            to={HOMEPAGE_PATH}
                            aria-label="Go to ArchiPro home page"
                            className="inline-block"
                        >
                            <ArchiProLogo className="h-auto w-[3.65rem]" />
                        </Link>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section
            ref={sectionRef}
            data-testid="closing-cta"
            className="relative w-full overflow-hidden"
        >
            {/* Part 1: Full-width image with staggered heading overlay */}
            <div
                className="relative w-full bg-[#c4c4c4]"
                style={{ aspectRatio: '1920 / 1076' }}
            >
                <img
                    ref={imageRef}
                    src={heroImage}
                    alt="Home Design Evening"
                    className="absolute inset-0 h-full w-full object-cover"
                    style={{
                        transform: `translate3d(0, ${offsetY}px, 0)`,
                    }}
                />
                <div className="absolute inset-0">
                    <HDEDesktopFrame className="h-full">
                        <div className="flex h-full flex-col justify-end pb-[12.5rem] pl-[13rem]">
                            <ScrollReveal animation="fade-up" duration={600}>
                                <p className="font-sans text-[4.335rem] font-medium uppercase not-italic leading-[0.9] tracking-[-0.13rem] text-white">
                                    YOUR BUILDING JOURNEY
                                </p>
                            </ScrollReveal>
                            <ScrollReveal
                                animation="fade-up"
                                duration={600}
                                delay={200}
                            >
                                <p
                                    className="font-sans text-[4.335rem] font-medium uppercase not-italic leading-[0.9] tracking-[-0.13rem] text-white"
                                    style={{ paddingLeft: '33.75rem' }}
                                >
                                    STARTS WITH ONE EVENING.
                                </p>
                            </ScrollReveal>
                        </div>
                    </HDEDesktopFrame>
                </div>
            </div>

            {/* Part 2: Dark footer — HDE letters left, event details right */}
            <div
                className="relative w-full bg-[#030c0b]"
                style={{ height: '54.5625rem' }}
            >
                <HDEDesktopFrame className="relative h-full">
                    {/* HDE Letters — left side, vertically centred */}
                    <ScrollReveal
                        animation="fade-up"
                        duration={800}
                        delay={200}
                        className="absolute"
                        style={{
                            left: '4rem',
                            bottom: '12.6rem',
                            width: '28.875rem',
                            height: 'auto',
                        }}
                    >
                        <HdeLettersSvg />
                    </ScrollReveal>

                    {/* HOME DESIGN EVENING. wordmark */}
                    <ScrollReveal
                        animation="fade-right"
                        duration={700}
                        delay={300}
                        className="absolute"
                        style={{
                            right: '12.3rem',
                            top: '16rem',
                            width: '54.875rem',
                            height: 'auto',
                        }}
                    >
                        <TitleSvg />
                    </ScrollReveal>

                    {/* Event details block */}
                    <div
                        className={cn(
                            'absolute flex flex-col transition-all ease-out [transition-duration:400ms]',
                            inView
                                ? 'translate-y-0 opacity-100'
                                : 'translate-y-5 opacity-0',
                        )}
                        style={{ left: '85.4rem', top: '20.1rem' }}
                    >
                        {/* Date and location */}
                        <div className="flex flex-col">
                            <p className="font-display text-[2.654rem] font-medium uppercase leading-[0.9] tracking-[-0.08rem] text-[#f0f0f0]">
                                {eventDateText}
                            </p>
                            <p className="font-display text-[2.654rem] font-medium uppercase leading-[0.9] tracking-[-0.08rem] text-[#f0f0f0]">
                                {eventLocationText}
                            </p>
                        </div>

                        {/* Register CTA */}
                        <button
                            type="button"
                            onClick={onRegister}
                            className={`group mt-[4.6rem] inline-flex items-center gap-[0.625rem] active:scale-[0.97] ${showRegisterButton ? '' : 'pointer-events-none invisible'}`}
                        >
                            <span className="font-display text-[2.654rem] font-medium uppercase leading-[0.9] tracking-[-0.08rem] text-[#f0f0f0]">
                                REGISTER NOW
                            </span>
                            <span className="hde-arrow-pulse hde-register-arrow inline-flex">
                                <ArrowSvg
                                    className="rotate-180 transition-transform duration-200 group-hover:translate-x-1"
                                    style={{
                                        width: '2.063rem',
                                        height: '1.806rem',
                                    }}
                                />
                            </span>
                        </button>

                        {/* Labels */}
                        <div className="mt-[2.25rem] flex flex-col gap-[0.25rem]">
                            <p className="font-display text-[1.188rem] font-medium uppercase leading-[0.9] tracking-[-0.036rem] text-[#f0f0f0]">
                                FREE ENTRY
                            </p>
                            <p className="font-display text-[1.188rem] font-medium uppercase leading-[0.9] tracking-[-0.036rem] text-[#f0f0f0]">
                                LIMITED SPACES
                            </p>
                        </div>
                    </div>

                    {showPresentedBySponsor ? (
                        <div className="absolute bottom-24 right-[26.5rem] flex items-center gap-[1.8rem]">
                            <p className="mt-[2.75rem] font-sans text-[0.902rem] font-medium uppercase leading-[0.9] tracking-[0.05em] text-[#f0f0f0]">
                                Presented by
                            </p>
                            <HDEPresentedByMark
                                logoUrl={presentedByLogoUrl}
                                profilePath={presentedByProfilePath}
                                sponsorName={presentedBySponsorName}
                                imageClassName="max-h-[3.25rem] w-[12rem]"
                                fallbackLogoClassName="h-auto w-[12rem] text-[#f0f0f0]"
                            />
                        </div>
                    ) : null}

                    {/* BY ARCHIPRO — bottom right */}
                    <div
                        className="absolute flex items-center gap-3"
                        style={{ right: '4.75rem', bottom: '5.4rem' }}
                    >
                        <span className="mt-9 font-sans text-[0.902rem] font-medium uppercase leading-[0.9] text-[#f0f0f0]">
                            BY ARCHIPRO
                        </span>
                        <Link
                            to={HOMEPAGE_PATH}
                            aria-label="Go to ArchiPro home page"
                            className="inline-block"
                        >
                            <ArchiProLogo />
                        </Link>
                    </div>
                </HDEDesktopFrame>
            </div>
        </section>
    );
};
